import React from 'react';
import { Award, GraduationCap, Briefcase, CheckCircle, Phone, Zap, Shield, Users, Stethoscope } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { clinicData } from '../data';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const handleWhatsAppCall = () => {
    window.open(`https://wa.me/91${clinicData.contact.whatsappNumber}?text=Hello, I would like to book an appointment`, '_blank');
  };

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>About - {clinicData.name} | Men's Health & Andrology</title>
        <meta name="description" content={clinicData.seo.about.description} />
        <link rel="canonical" href="https://www.manclinics.com/about" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 via-white to-pink-50 relative overflow-hidden">
        <div className="absolute top-10 right-10 w-96 h-96 bg-[#D91656]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-80 h-80 bg-[#1E5A8E]/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block bg-gradient-to-r from-[#1E5A8E]/10 to-[#D91656]/10 text-[#1E5A8E] px-6 py-3 rounded-full text-sm font-semibold backdrop-blur-sm border border-[#1E5A8E]/20 mb-6">
              About Us
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              {clinicData.name}
            </h1>
            <p className="text-2xl text-[#D91656] font-medium mb-6">
              {clinicData.tagline}
            </p>
            <p className="text-gray-600 leading-relaxed text-lg">
              {clinicData.about.content}
            </p>
          </div>
        </div>
      </section>

      {/* Special Technologies */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Special <span className="text-[#D91656]">Technologies</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {clinicData.specialTechnologies.map((tech, index) => {
              const IconComponent = index === 0 ? Zap : Shield;
              return (
                <Card key={index} className="border-2 border-[#D91656]/20 hover:border-[#D91656] transition-colors hover:shadow-xl group">
                  <CardContent className="p-8 text-center space-y-4">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#D91656] to-[#B01348] rounded-full group-hover:scale-110 transition-transform">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{tech.name}</h3>
                    <p className="text-gray-600">{tech.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Doctors */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Our <span className="text-[#D91656]">Doctors</span>
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            A team of highly qualified Genitourinary Surgeons dedicated to men's health
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {clinicData.doctors.map((doctor, index) => (
              <Card key={index} className="border-none shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group">
                <div className="relative h-72 overflow-hidden">
                  <img
                    src={doctor.image}
                    alt={doctor.name}
                    onError={(e) => {
                      e.target.src = "https://placehold.co/400x500/e2e8f0/64748b/?text=Doctor";
                      e.target.onerror = null;
                    }}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                </div>
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{doctor.name}</h3>
                  <p className="text-[#D91656] font-medium text-sm mb-1">{doctor.qualifications}</p>
                  <p className="text-gray-600 text-sm">{doctor.designation}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Allied Services */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our <span className="text-[#D91656]">Allied Services</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {clinicData.alliedServices.map((service, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border border-gray-100"
              >
                <div className="flex-shrink-0 mt-1">
                  <div className="w-8 h-8 bg-[#D91656]/10 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-[#D91656]" />
                  </div>
                </div>
                <p className="text-gray-700 font-medium">{service.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BPH Procedures */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Procedures for BPH <span className="text-[#D91656]">(Prostate Growth)</span>
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Minimally Invasive Surgical Therapies for prostate conditions
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {clinicData.bphProcedures.map((procedure, index) => (
              <Card key={index} className="border-l-4 border-[#1E5A8E] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900">{procedure.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Prosthetic Urology */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Prosthetic <span className="text-[#D91656]">Urology</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {clinicData.prostheticUrology.map((procedure, index) => (
              <Card key={index} className="border-l-4 border-[#D91656] hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 text-lg">{procedure.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#1E5A8E] to-[#D91656]">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Schedule Your Confidential Consultation
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Take the first step towards better health. Book your appointment with our specialists today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleWhatsAppCall}
              size="lg"
              className="bg-white text-[#D91656] hover:bg-gray-100 text-lg px-8 py-6"
            >
              Book Appointment via WhatsApp
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 text-lg px-8 py-6"
              onClick={() => window.location.href = `tel:${clinicData.contact.phone}`}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call {clinicData.contact.phone}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;

